/* The title screen: the menu in a pane, the workshop (see workshop.ts) drawn beside it (or
   above it on tall screens) in the rest of the canvas (see title.tsx). */

.screen {
  position: fixed;
  inset: 0;
  pointer-events: none;
  transition: opacity 0.4s, visibility 0.4s;
  z-index: 10;
}

.hidden {
  opacity: 0;
  visibility: hidden;
}

.pane {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: 42%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 28px 28px 0;
  overflow: hidden;
  background: linear-gradient(180deg, #fff8ee 0%, #f6e6d2 100%);
  border-right: 1px solid var(--line);
  box-shadow: 4px 0 24px rgba(120, 80, 50, 0.12);
  pointer-events: auto;
}

/* The logo and the menu sit a little down from the top: the room left over goes a third above
   them and two thirds below the menu (see .menu), down to the kitten. */
.pane::before {
  content: "";
  flex: 1;
}

.heading {
  width: min(420px, 100%);
  margin: 0;
  line-height: 0;
}

.logo {
  display: block;
  width: 100%;
  height: auto;
}

.lead {
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  text-wrap: balance;
  text-align: center;
  color: var(--ink-soft);
}

.menu {
  flex: 2 1 auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
  width: min(320px, 100%);
  margin-top: 6px;
}

/* A menu item: pointing at it moves the workshop's camera to its spot. */
.item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  padding: 12px 18px 13px;
  border: 1px solid var(--line);
  border-left: 6px solid var(--accent);
  border-radius: 14px;
  background: #fffaf3;
  font: inherit;
  text-align: left;
  color: var(--ink);
  cursor: pointer;
  box-shadow: 0 3px 10px rgba(120, 80, 50, 0.1);
  transition: transform 0.15s, box-shadow 0.15s, border-color 0.15s;
}

.item b {
  font-size: 18px;
}

.item small {
  font-size: 12px;
  color: var(--ink-soft);
}

/* The clay left kneading, first in the menu. */
.resume {
  border-left-color: var(--mint);
  background: #f2fbf7;
}

.item:hover,
.item:focus-visible {
  transform: translateX(6px);
  border-left-color: var(--mint);
  box-shadow: 0 6px 16px rgba(120, 80, 50, 0.16);
}

/* How to play, the settings and the credits: small, in a row under the menu. */
.extras {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 2px;
}

.extra {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px 6px 9px;
  border: 1px solid var(--line);
  border-radius: 99px;
  background: #fffaf3;
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  color: var(--ink-soft);
  white-space: nowrap;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(120, 80, 50, 0.08);
  transition: transform 0.15s, color 0.15s, border-color 0.15s;
}

.extra svg {
  width: 18px;
  height: 18px;
}

.extra:hover,
.extra:focus-visible {
  transform: translateY(-2px);
  border-color: var(--mint);
  color: var(--ink);
}

/* ---------- The kneading kitten ---------- */

.kitten {
  position: relative;
  flex: none;
  width: 180px;
  height: 173px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  touch-action: manipulation;
}

.kitten svg {
  width: 100%;
  height: 100%;
  overflow: visible;
}

.kitten svg * {
  transform-box: fill-box;
}

.body {
  transform-origin: 50% 100%;
}

.paw {
  transform-origin: 50% 100%;
  animation: knead 0.9s ease-in-out infinite;
}

.pawRight {
  animation-delay: -0.45s;
}

.dough {
  transform-origin: 50% 100%;
  animation: dough 0.45s ease-in-out infinite;
}

.tail {
  transform-origin: 0% 100%;
  animation: tail 2.4s ease-in-out infinite;
}

.eyesOpen {
  transform-origin: 50% 50%;
  animation: blink 4s infinite;
}

.eyesSquint {
  opacity: 0;
}

.earLeft {
  transform-origin: 80% 100%;
}

.earRight {
  transform-origin: 20% 100%;
}

.poked .body {
  animation: squish 0.55s cubic-bezier(0.3, 0.7, 0.4, 1.4);
}

.poked .eyesOpen {
  animation: disappear 0.7s steps(1);
}

.poked .eyesSquint {
  animation: appear 0.7s steps(1);
}

.poked .earLeft {
  animation: twitch-left 0.35s ease-in-out 2;
}

.poked .earRight {
  animation: twitch-right 0.35s ease-in-out 2;
}

.bubble {
  position: absolute;
  top: -6px;
  right: -34px;
  padding: 5px 11px;
  border-radius: 14px;
  background: #fff;
  border: 1px solid var(--line);
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
  opacity: 0;
  transform: scale(0.6);
  pointer-events: none;
}

.show {
  animation: bubble 1.3s ease-out forwards;
}

@keyframes knead {
  0%,
  100% {
    transform: translateY(0) scale(1, 1);
  }
  50% {
    transform: translateY(-7px) scale(0.95, 1.05);
  }
}

@keyframes dough {
  0%,
  100% {
    transform: scale(1, 1);
  }
  50% {
    transform: scale(1.04, 0.9);
  }
}

@keyframes tail {
  0%,
  100% {
    transform: rotate(0deg);
  }
  50% {
    transform: rotate(-9deg);
  }
}

@keyframes blink {
  0%,
  94%,
  100% {
    transform: scaleY(1);
  }
  97% {
    transform: scaleY(0.1);
  }
}

@keyframes squish {
  0% {
    transform: scale(1, 1);
  }
  22% {
    transform: scale(1.16, 0.8);
  }
  48% {
    transform: scale(0.93, 1.09);
  }
  72% {
    transform: scale(1.03, 0.97);
  }
  100% {
    transform: scale(1, 1);
  }
}

/* The open eyes go while the squinting ones show, for the length of a poke. */
@keyframes disappear {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

@keyframes appear {
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}

@keyframes twitch-left {
  50% {
    transform: rotate(-14deg);
  }
}

@keyframes twitch-right {
  50% {
    transform: rotate(14deg);
  }
}

@keyframes bubble {
  0% {
    opacity: 0;
    transform: scale(0.6) translateY(6px);
  }
  15% {
    opacity: 1;
    transform: scale(1.08) translateY(0);
  }
  25% {
    transform: scale(1);
  }
  80% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: translateY(-6px);
  }
}

/* Where the pane is short (tall screens, or wide ones not tall enough for the whole menu): the
   menu at the top, two to a row, their names only. */
@media (max-aspect-ratio: 1/1), (max-height: 740px) {
  .pane::before {
    flex: none;
  }

  .menu {
    flex: none;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    width: min(420px, 100%);
  }

  .extras,
  .resume {
    grid-column: 1 / -1;
  }

  .item {
    padding: 9px 12px 10px;
  }

  .item b {
    font-size: 16px;
  }

  .item small {
    display: none;
  }
}

/* Tall screens: the pane along the bottom, the workshop above it, the kitten in its corner. On
   short ones the pane keeps its height and the workshop has less: room for the whole menu, the
   clay left kneading at its top, with the kitten clear below it. */
@media (max-aspect-ratio: 1/1) {
  .pane {
    top: auto;
    right: 0;
    width: auto;
    height: max(46%, 430px);
    gap: 10px;
    padding: 16px 16px 0;
    border-right: none;
    border-top: 1px solid var(--line);
    box-shadow: 0 -4px 24px rgba(120, 80, 50, 0.12);
  }

  .heading {
    width: min(300px, 80%);
  }

  .kitten {
    position: absolute;
    right: 4px;
    bottom: 0;
    width: 80px;
    height: 77px;
  }

  /* On the kitten's left, the screen's edge being on its right. */
  .bubble {
    right: auto;
    left: -34px;
  }
}
